import { useState } from 'react';
import { useMarketingData } from '../MarketingContext';
import { ResearchRecord, ResearchType } from '../types';

export default function ResearchPage() {
  const { companies, research, updateResearch } = useMarketingData();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [selected, setSelected] = useState<ResearchRecord | null>(null);

  const types: ResearchType[] = ['market', 'competitor', 'customer', 'industry_trends', 'product', 'pricing', 'content_gap', 'swot', 'pestle', 'porters_five_forces'];
  const typeLabels: Record<string, string> = { market: '🔬 Market', competitor: '⚔️ Competitor', customer: '👥 Customer', industry_trends: '📈 Trends', product: '📦 Product', pricing: '💰 Pricing', content_gap: '📝 Content Gap', swot: '📊 SWOT', pestle: '🌍 PESTLE', porters_five_forces: '⚡ Porter\'s' };

  const filtered = research.filter(r => {
    if (search && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType !== 'all' && r.type !== filterType) return false;
    return true;
  });

  if (selected) {
    return (
      <div className="space-y-4">
        <button onClick={() => setSelected(null)} className="text-sm text-blue-600 hover:underline">← Back to Research</button>
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">{typeLabels[selected.type] || selected.type}</div>
              <h2 className="text-xl font-bold text-gray-900">{selected.title}</h2>
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${selected.verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{selected.verified ? '✓ Verified' : '⏳ Unverified'}</span>
                <span className="text-xs text-gray-400">Reliability: {selected.reliability}/10</span>
                <span className="text-xs text-gray-400">📅 {selected.researchDate}</span>
              </div>
            </div>
            <button onClick={() => updateResearch(selected.id, { verified: !selected.verified })} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${selected.verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
              {selected.verified ? '✓ Verified' : 'Mark Verified'}
            </button>
          </div>
          <div className="space-y-4">
            <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Summary</h3><p className="text-sm text-gray-600">{selected.summary}</p></div>
            <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Key Findings</h3><p className="text-sm text-gray-600">{selected.findings}</p></div>
            <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Business Impact</h3><p className="text-sm text-gray-600">{selected.impact}</p></div>
            <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Recommendations</h3><p className="text-sm text-gray-600">{selected.recommendations}</p></div>
            <div className="grid grid-cols-2 gap-4">
              <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Source</h3><p className="text-sm text-gray-600">{selected.sourceTitle || 'Internal analysis'}</p></div>
              <div><h3 className="text-sm font-semibold text-gray-900 mb-1">Tags</h3><div className="flex flex-wrap gap-1">{selected.tags.map(t => <span key={t} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{t}</span>)}</div></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900">Market Research</h1><p className="text-sm text-gray-500">Market, competitor, customer, and industry research</p></div>
      </div>

      <div className="flex gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search research..." className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg" />
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5">
          <option value="all">All Types</option>{types.map(t => <option key={t} value={t}>{typeLabels[t]}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {filtered.map(r => {
          const co = companies.find(c => c.id === r.companyId);
          return (
            <button key={r.id} onClick={() => setSelected(r)} className="w-full bg-white border border-gray-200 rounded-xl p-4 text-left hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="text-lg">{typeLabels[r.type]?.split(' ')[0] || '🔬'}</div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-gray-900">{r.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{r.summary.slice(0, 120)}...</div>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                    <span>{typeLabels[r.type]?.split(' ')[1]}</span>
                    <span>🏢 {co?.name}</span>
                    <span>Reliability: {r.reliability}/10</span>
                    <span>📅 {r.researchDate}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{r.verified ? 'Verified' : 'Unverified'}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
